import {GameClientUserState} from '@colonist/game-base-types'
import {GameState, NestedReadonly, PlayerColor, TradeResponseStatus} from '@colonist/game-states'
import {PlayerValidator} from './PlayerValidator'
import {CurrentStateValidator} from './CurrentStateValidator'
import {MapValidator} from './map/MapValidator'

export class GameValidator {
    private readonly gameState: NestedReadonly<GameState>
    private readonly userStates: NestedReadonly<GameClientUserState>[]
    readonly mapValidator: MapValidator
    readonly currentStateValidator: CurrentStateValidator
    readonly playerValidators: Map<PlayerColor, PlayerValidator> = new Map()

    constructor(gameState: NestedReadonly<GameState>, userStates: NestedReadonly<GameClientUserState>[]) {
        this.gameState = gameState
        this.userStates = userStates
        this.mapValidator = new MapValidator(gameState.mapState)

        const experimentalCurrentState = gameState.experimentalMechanicState?.currentState
        const mechanicTurnColonistRushState = gameState.experimentalMechanicState?.mechanicTurnColonistRushState
        const mechanicRobberColonistRushState = gameState.experimentalMechanicState?.mechanicRobberColonistRushState
        const playerStates = gameState.experimentalMechanicState?.playerStates
        const playerColorKeys = Array.from(this.playerValidators.keys())
        this.currentStateValidator = new CurrentStateValidator(gameState.currentState, experimentalCurrentState, mechanicTurnColonistRushState, playerStates, playerColorKeys, mechanicRobberColonistRushState)

        for(const playerColorKey in this.gameState.playerStates) {
            const playerColor = Number(playerColorKey) as PlayerColor

            const playerState = this.gameState.playerStates[playerColor]
            if(playerState == undefined) continue

            const userState = this.userStates.find((playerUserState) => playerUserState.selectedColor === playerColor)
            if(userState == undefined) continue

            const playerStateController = new PlayerValidator(playerState, userState)
            this.playerValidators.set(playerStateController.color, playerStateController)
        }
    }

    getPlayerValidator(color: PlayerColor): PlayerValidator {
        const playerValidator = this.playerValidators.get(color)
        if(playerValidator == undefined) throw new Error(`PlayerValidator for color ${PlayerColor[color]} not found`)

        return playerValidator
    }

    getPlayersTakingAction(): PlayerColor[] {
        const playersTakingOutOfTurnAction: PlayerColor[] = []
        for(const player of this.playerValidators.values()) {
            if(!this.isPlayerTakingOutOfTurnAction(player.color)) continue
            playersTakingOutOfTurnAction.push(player.color)
        }
        if(playersTakingOutOfTurnAction.length == 0) return this.currentStateValidator.currentTurnPlayers

        return playersTakingOutOfTurnAction
    }

    isPlayerTakingOutOfTurnAction(player: PlayerColor): boolean {
        const playerState = this.gameState.playerStates[player]
        if(playerState === undefined) return false

        if(playerState.isTakingAction) return true

        const isWaitingForOpponentTradeResponses =
            Object.keys(this.gameState.tradeState.activeOffers).length > 0
            && this.currentStateValidator.currentTurnPlayers.includes(player)
        if(isWaitingForOpponentTradeResponses) return true

        for(const activeTradeState of Object.values(this.gameState.tradeState.activeOffers)) {
            if(activeTradeState == undefined) continue

            if(activeTradeState.playersCreatingCounterOffer[player] == true) return true
            if(activeTradeState.playerResponses[player] == TradeResponseStatus.PendingResponse) return true
        }

        return false
    }
}
